/**
 * WHAT MAKES AN ANSWER DURABLE — save, pin, share, and say whether it helped.
 *
 * ══ THE ORDER IS A DEPENDENCY, NOT A WORKFLOW OPINION ══════════════════════
 *
 * A share token lives on the PIN row, and a pin points at a SAVED QUESTION. So
 * the three buttons appear in that order and each unlocks the next. That is not
 * ceremony: it is why "revoke" needs no endpoint — deleting the pin kills every
 * link to it, one lifecycle, one row, one act.
 *
 * ══ AND WHY A SAVE STORES A QUESTION RATHER THAN AN ANSWER ═════════════════
 *
 * 🔴 The row holds `metric_id` + typed params + a date-range spec. It holds NO
 * NUMBER. Re-opening re-executes under the reader's own permissions, so a saved
 * question cannot age into a figure that contradicts the dashboard, and a share
 * link cannot hand somebody data their role withholds. Both properties are
 * structural — there is no numeric column to abuse — and this component must
 * never start caching one locally to make the list feel faster.
 *
 * ══ THE LABEL IS DURABLE FREE TEXT ═════════════════════════════════════════
 *
 * It passes the pre-model input gate server-side, which CAN refuse it — a label
 * carrying an e-mail address is rejected and NO ROW IS WRITTEN. The refusal is
 * surfaced verbatim rather than swallowed, because a save that silently did
 * nothing is the worst of the three outcomes.
 */
import { useState } from 'react';
import { Bookmark, Check, Copy, Pin, Share2, ThumbsDown, ThumbsUp } from 'lucide-react';

import type { ArtifactsController } from '@/hooks/useAgentArtifacts';
import { lockedActionHint } from '../answer-view';
import type { SaveBody } from '../save-question';

export function AnswerActions({
  artifacts,
  turnId,
  saveBody,
  defaultLabel,
  rowLevelAnswer = false,
}: {
  artifacts: ArtifactsController;
  turnId: string | null;
  /** The question this answer answers — `saveBodyFrom`. `null` disables Save. */
  saveBody: SaveBody | null;
  defaultLabel: string;
  /**
   * The answer read member records (`database_query`). Such an answer has a result, but by design
   * it cannot be saved, pinned or shared, so "no query result" would be false (#3535).
   */
  rowLevelAnswer?: boolean;
}) {
  const [label, setLabel] = useState(defaultLabel.slice(0, 200));
  const [savedId, setSavedId] = useState<string | null>(null);
  const [pinId, setPinId] = useState<string | null>(null);
  const [sharePath, setSharePath] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [verdict, setVerdict] = useState<'up' | 'down' | null>(null);
  const [busy, setBusy] = useState(false);

  const canSave = saveBody !== null && label.trim().length > 0 && !busy;

  const save = async () => {
    if (saveBody === null) return;
    setBusy(true);
    const row = await artifacts.saveQuestion({ label: label.trim(), ...saveBody });
    setBusy(false);
    if (row) setSavedId(row.id);
  };

  const pin = async () => {
    if (savedId === null) return;
    setBusy(true);
    const id = await artifacts.pinAnswer(savedId, label.trim());
    setBusy(false);
    if (id) setPinId(id);
  };

  const share = async () => {
    if (pinId === null) return;
    setBusy(true);
    const path = await artifacts.shareAnswer(pinId);
    setBusy(false);
    if (path) setSharePath(path);
  };

  const copy = async () => {
    if (sharePath === null) return;
    const absolute = `${window.location.origin}${sharePath}`;
    try {
      await navigator.clipboard.writeText(absolute);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the path is on screen either way.
      setCopied(false);
    }
  };

  const vote = async (next: 'up' | 'down') => {
    if (turnId === null) return;
    setVerdict(next);
    await artifacts.recordFeedback(turnId, next);
  };

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          maxLength={200}
          aria-label="Label for the saved question"
          placeholder="Name this question"
          className="min-w-[14rem] flex-1 rounded-md border bg-background px-2 py-1 text-xs outline-none"
        />
        <button
          type="button"
          onClick={() => void save()}
          disabled={!canSave || savedId !== null}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors hover:bg-muted disabled:opacity-50"
        >
          <Bookmark className="h-3 w-3" aria-hidden />
          {savedId === null ? 'Save question' : 'Saved'}
        </button>
        <button
          type="button"
          onClick={() => void pin()}
          disabled={savedId === null || pinId !== null || busy}
          title={lockedActionHint('pin', { saved: savedId !== null, pinned: pinId !== null })}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors hover:bg-muted disabled:opacity-50"
        >
          <Pin className="h-3 w-3" aria-hidden />
          {pinId === null ? 'Pin to dashboard' : 'Pinned'}
        </button>
        <button
          type="button"
          onClick={() => void share()}
          disabled={pinId === null || sharePath !== null || busy}
          title={lockedActionHint('share', { saved: savedId !== null, pinned: pinId !== null })}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors hover:bg-muted disabled:opacity-50"
        >
          <Share2 className="h-3 w-3" aria-hidden />
          {sharePath === null ? 'Create share link' : 'Shared'}
        </button>
        <span className="ml-auto inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => void vote('up')}
            aria-pressed={verdict === 'up'}
            aria-label="This answer was useful"
            disabled={turnId === null}
            className={`rounded-md border p-1 transition-colors hover:bg-muted disabled:opacity-40 ${
              verdict === 'up' ? 'bg-primary text-primary-foreground' : ''
            }`}
          >
            <ThumbsUp className="h-3 w-3" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => void vote('down')}
            aria-pressed={verdict === 'down'}
            aria-label="This answer was wrong or unhelpful"
            disabled={turnId === null}
            className={`rounded-md border p-1 transition-colors hover:bg-muted disabled:opacity-40 ${
              verdict === 'down' ? 'bg-destructive text-destructive-foreground' : ''
            }`}
          >
            <ThumbsDown className="h-3 w-3" aria-hidden />
          </button>
        </span>
      </div>

      {saveBody === null && (
        <p className="text-[11px] text-muted-foreground">
          {rowLevelAnswer
            ? 'An answer read from member records cannot be saved, pinned or shared. Only catalogue answers can.'
            : 'This answer has no query result to save.'}
        </p>
      )}

      {sharePath !== null && (
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="rounded bg-background px-2 py-1 font-mono">{sharePath}</span>
          <button
            type="button"
            onClick={() => void copy()}
            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 transition-colors hover:bg-muted"
          >
            {copied ? <Check className="h-3 w-3" aria-hidden /> : <Copy className="h-3 w-3" aria-hidden />}
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <span className="text-muted-foreground">
            Admin-to-admin. The recipient re-runs it under their own permissions and may see
            less than you do.
          </span>
        </div>
      )}

      {artifacts.error && (
        <p className="text-[11px] text-red-600 dark:text-red-400" role="alert">
          {artifacts.error}
        </p>
      )}
    </div>
  );
}
